import { useState, useContext, useEffect } from "react";
import { useQuery } from "react-query"; 
import { Row } from "react-bootstrap";

import { UserContext } from "../../Contexts";
import { useToast } from "../../Hooks";
import { StatisticService, StatisticLabels, SurveyService } from "../../Services";
import { DataSetRatio, HomeStatisticBarSkeleton } from "../../Components";
import { DateHelpers } from "../../Helpers";

/**
 * HomeStatisticBar component displays participant progress statistics in a visual bar format.
 *
 * - Fetches user-specific statistics using `StatisticService`.
 * - If an active survey is found, it calculates and shows how many days are left to complete it.
 * - Renders a `DataSetRatio` component with labeled segments for progress states:
 *   "Not Started", "In Progress", and "Completed".
 *
 * @component
 * 
 * @returns {JSX.Element} A responsive row containing progress statistics or a loading placeholder.
 *
 * @example
 * <HomeStatisticBar />
 */
const HomeStatisticBar = () => {
  const { user } = useContext(UserContext);
  const { showError } = useToast();
  const [daysLeftToComplete, setDaysLeftToComplete] = useState(null);

  const { data: statistic, isLoading: isStatisticDataLoading, isError: isStatisticDataError } = useQuery(
    "statisticData",
    () => StatisticService.getByType(StatisticService.resolveTypeForUser(user)),
    {
      onError: () => {
        showError("Wystąpił błąd podczas pobierania statystyk. Wykres nie może zostać wyświetlony.");
      },
    }
  );

  const title = statistic?.title ?? null;
  const type = title ? StatisticService.resolveQuestionnaireTypeForStatisticName(title) : null;

  const enabledSurveyQuery = Boolean(user?.id) && Boolean(title) && type !== null;

  useEffect(() => {
    if (!enabledSurveyQuery) {
      setDaysLeftToComplete(null);
    }
  }, [enabledSurveyQuery]);

  useQuery(
    ["surveyActiveData", title, type],
    async () => {
      const res = await SurveyService.checkIsSurveyActive(type, user.id);
      return res.data ?? null;
    },
    {
      enabled: enabledSurveyQuery,
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response?.dateTo != null && response?.dateTo != 'null') {
          const calculatedTimeLeft = DateHelpers.getStringDeadlineTime(new Date(response.dateTo));
          setDaysLeftToComplete(calculatedTimeLeft);
        } else {
          setDaysLeftToComplete(null);
        }
      },
      onError: () => {
        setDaysLeftToComplete(null);
      },
    }
  );

  if (isStatisticDataLoading) {
    return (
      <Row className="px-4">
        <HomeStatisticBarSkeleton />
      </Row>
    );
  }

  if (isStatisticDataError || !statistic) {
    return (
      <Row className="px-4">
      </Row>
    );
  }

  return (
    <Row className="p-2">
      <DataSetRatio
        heading={StatisticLabels.resolveHeadingLabel(statistic["title"], {
          percent: statistic["clients-percent-completed"],
          daysLeft: daysLeftToComplete,
        })}
        label={<p>Postęp uczestników</p>}
        states={[
          {
            name: StatisticLabels.resolveNotStartedLabelForLegend(statistic["title"]),
            value: statistic["clients-not-started"],
            selected: StatisticLabels.isActiveStateForUserStep(statistic["title"], user.clientStep, "started"),
          },
          {
            name: StatisticLabels.resolveInProgressLabelForLegend(),
            value: statistic["clients-in-progress"],
            selected: StatisticLabels.isActiveStateForUserStep(statistic["title"], user.clientStep, "in-progress"),
          },
          {
            name: StatisticLabels.resolveCompletedLabelForLegend(statistic["title"]),
            value: statistic["clients-completed"],
            selected: StatisticLabels.isActiveStateForUserStep(statistic["title"], user.clientStep, "completed"),
          },
        ]}
      />
    </Row>
  );
};

export default HomeStatisticBar;
